'use client'

import {useContext} from 'react'
import {PublicEntryChromeContext} from './PublicEntryChromeContext'
import {publicSans} from '../../../lib/public-font'
import {useTranslation} from '../../../hooks/useTranslation'
import {cn} from '../../../lib/utils'

import {publicEntryStyles, publicPricingStyles, publicScheduleStyles, publicTermsStyles} from './publicEntryStyles'
import type {PublicSitePage} from './publicSiteNavigation'

const pulse = 'motion-safe:animate-pulse motion-reduce:animate-none'

export function PublicEntryLoading({page = 'schedule'}: {page?: PublicSitePage}) {
    const {t} = useTranslation('landing')

    const inherited = useContext(PublicEntryChromeContext)
    const content = (
                <div className={cn(publicEntryStyles.content, getPublicLoadingPadding(page, Boolean(inherited)))} aria-hidden="true" data-public-loading-page={page}>
                    <PublicPageSkeleton page={page} />
                </div>
    )
    if (inherited) return <div role="status" aria-live="polite" aria-label={t('publicEntry.loading')}>{content}</div>
    return (
        <main
            className={cn(publicSans.variable, publicEntryStyles.shellRoot, 'bg-white')}
            role="status"
            aria-live="polite"
            aria-label={t('publicEntry.loading')}
        >
            <div className={publicEntryStyles.shellBody}>
                <header className={publicEntryStyles.header}>
                    <div className={cn(publicEntryStyles.content, publicEntryStyles.headerInner)}>
                        <div className={cn('h-6 w-40 rounded-full bg-[var(--landing-primary-100)]', pulse)} />
                        <div className="hidden items-center gap-8 lg:flex" aria-hidden="true">
                            <div className={cn('h-4 w-16 rounded-full bg-slate-100', pulse)} />
                            <div className={cn('h-4 w-20 rounded-full bg-slate-100', pulse)} />
                            <div className={cn('h-4 w-14 rounded-full bg-slate-100', pulse)} />
                        </div>
                        <div className={cn('h-10 w-10 rounded-md bg-slate-100 lg:w-28 lg:rounded-full', pulse)} />
                    </div>
                </header>

                {content}
            </div>
        </main>
    )
}

function SkeletonBlock({className}: {className: string}) {
    return <div className={cn('rounded-md bg-slate-100', pulse, className)} />
}

function PublicScheduleSkeleton() {
    return (
        <>
            <div className={publicScheduleStyles.filterToolbar}>
                <div className={publicScheduleStyles.filterGrid}>
                    {[0, 1].map(filter => (
                        <div key={filter} className="flex h-12 flex-col justify-center gap-1 rounded-xl border border-[var(--landing-neutral-100)] bg-white px-3">
                            <SkeletonBlock className="h-3 w-16" />
                            <SkeletonBlock className="h-4 w-3/4" />
                        </div>
                    ))}
                </div>
                <div className={publicScheduleStyles.periodRow}>
                    <div className="min-w-0 flex-1">
                        <div className="flex h-12 items-center overflow-hidden rounded-xl border border-[var(--landing-neutral-100)] bg-white shadow-sm">
                            <SkeletonBlock className="mx-3 size-5 shrink-0" />
                            <div className="flex h-full min-w-0 flex-1 items-center justify-center border-x border-[var(--landing-neutral-100)]">
                                <SkeletonBlock className="h-4 w-48 max-w-[75%]" />
                            </div>
                            <SkeletonBlock className="mx-3 size-5 shrink-0" />
                        </div>
                    </div>
                    <SkeletonBlock className="h-12 w-16 shrink-0 !rounded-xl" />
                </div>
            </div>
            <div className={cn(publicScheduleStyles.calendarTopSpacing, 'overflow-hidden rounded-lg border border-slate-100 bg-white')}>
                <div className="min-w-[1470px] lg:min-w-full">
                    <div className="grid grid-cols-7">
                        {Array.from({length: 7}, (_, index) => (
                            <div key={index} className="flex min-h-[76px] items-center gap-3 border-r border-dashed border-[var(--landing-primary-100)] px-4 py-4 last:border-r-0">
                                <SkeletonBlock className="size-9 shrink-0 !rounded-full" />
                                <div className="min-w-0 flex-1 space-y-2">
                                    <SkeletonBlock className="h-3 w-2/3" />
                                    <SkeletonBlock className="h-4 w-full" />
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="grid min-h-[320px] grid-cols-7 divide-x divide-slate-100">
                        {Array.from({length: 7}, (_, index) => (
                            <div key={index} className="p-3">
                                <div className="space-y-3 rounded-lg border border-slate-100 p-3">
                                    <SkeletonBlock className="h-3 w-2/3" />
                                    <SkeletonBlock className="h-4 w-full" />
                                    <SkeletonBlock className="h-3 w-3/4" />
                                    <SkeletonBlock className="h-9 w-full bg-[var(--landing-primary-100)]" />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </>
    )
}

function PublicPrivateBookingsSkeleton() {
    return (
        <div className="-mx-2 grid gap-10 lg:mx-0 lg:grid-cols-[minmax(0,1fr)_320px] lg:items-start">
            <div>
                {Array.from({length: 4}, (_, index) => (
                    <div key={index} className="border-t border-[var(--landing-neutral-100)] pt-5 first:border-t-0 first:pt-0 lg:grid lg:grid-cols-[32px_minmax(0,1fr)] lg:items-start lg:gap-2 lg:py-6 lg:first:pt-0">
                        <div className="mb-2 flex items-center gap-3 lg:contents">
                            <SkeletonBlock className="size-7 shrink-0 !rounded-full bg-[var(--landing-primary-100)]" />
                            <SkeletonBlock className="my-1 h-4 w-36 lg:mb-2" />
                        </div>
                        <div className="min-w-0 lg:col-start-2">
                            {index === 2 ? (
                                <>
                                    <div className="flex h-9 items-center justify-between">
                                        <SkeletonBlock className="size-9" />
                                        <SkeletonBlock className="h-4 w-40" />
                                        <SkeletonBlock className="size-9" />
                                    </div>
                                    <div className="mt-4 grid grid-cols-7 gap-1">
                                        {Array.from({length: 7}, (_, day) => <SkeletonBlock key={day} className="h-14" />)}
                                    </div>
                                </>
                            ) : index === 3 ? <SkeletonBlock className="h-24 w-full !rounded-xl sm:h-28 lg:h-36" /> : (
                                <div className="flex min-h-14 items-center justify-between rounded-xl border border-[var(--landing-neutral-100)] bg-white px-3.5">
                                    <SkeletonBlock className="h-4 w-1/3" />
                                    <SkeletonBlock className="size-4" />
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>
            <div className="hidden rounded-xl border border-[var(--landing-neutral-100)] bg-white p-6 shadow-[0_10px_24px_rgba(57,98,198,0.06)] lg:block">
                <SkeletonBlock className="h-6 w-40" />
                <div className="mt-6 space-y-5">
                    {Array.from({length: 4}, (_, index) => (
                        <div key={index} className="flex gap-3.5">
                            <SkeletonBlock className="mt-0.5 size-9 shrink-0 !rounded-full" />
                            <div className="flex-1 space-y-2">
                                <SkeletonBlock className="h-3 w-20" />
                                <SkeletonBlock className="h-4 w-3/4" />
                            </div>
                        </div>
                    ))}
                </div>
                <div className="mt-7 flex flex-col items-center gap-2 border-t border-slate-200 pt-6">
                    <SkeletonBlock className="h-3 w-12" />
                    <SkeletonBlock className="h-7 w-24" />
                </div>
                <SkeletonBlock className="mt-4 h-12 w-full bg-[var(--landing-primary-100)]" />
            </div>
        </div>
    )
}

function PublicOfferCardsSkeleton({page}: {page: 'courses' | 'events'}) {
    const isCourse = page === 'courses'
    return (
        <div className="space-y-4">
            {Array.from({length: 3}, (_, index) => (
                <div key={index} className="overflow-hidden rounded-[14px] border border-[var(--landing-neutral-100)] bg-white shadow-[0_12px_28px_rgba(57,98,198,0.07)]">
                    <div className={cn('grid min-w-0 lg:min-h-[225px]', isCourse ? 'lg:grid-cols-[360px_minmax(0,1fr)_300px]' : 'lg:grid-cols-[360px_minmax(0,1fr)_230px]')}>
                        <SkeletonBlock className={cn('aspect-[16/10] w-full !rounded-none', isCourse ? 'lg:aspect-auto lg:h-full' : 'lg:self-center')} />
                        <div className="min-w-0 p-5 md:px-6 md:py-5">
                            <SkeletonBlock className="h-7 w-3/4 bg-[var(--landing-primary-100)]" />
                            <SkeletonBlock className="mt-3 h-4 w-full" />
                            <div className="mt-4 space-y-2.5">
                                {Array.from({length: 3}, (_, row) => (
                                    <div key={row} className="flex items-center gap-2">
                                        <SkeletonBlock className="size-5 shrink-0 !rounded-full" />
                                        <SkeletonBlock className="h-4 w-1/2" />
                                    </div>
                                ))}
                            </div>
                            {!isCourse ? (
                                <div className="mt-5 border-t border-[var(--landing-neutral-100)] pt-4 lg:hidden">
                                    <div className="mb-4 flex justify-between gap-4">
                                        <SkeletonBlock className="h-5 w-1/2" />
                                        <SkeletonBlock className="h-5 w-16" />
                                    </div>
                                    <SkeletonBlock className="h-11 w-full bg-[var(--landing-primary-100)]" />
                                </div>
                            ) : null}
                        </div>
                        <div className={cn('flex-col border-[var(--landing-neutral-100)] bg-[var(--landing-neutral-50)] px-5 py-4 lg:flex lg:border-l', isCourse ? 'flex border-t lg:border-t-0' : 'hidden')}>
                            <SkeletonBlock className="h-3 w-24" />
                            <SkeletonBlock className="mt-2 h-5 w-3/4" />
                            {isCourse ? <SkeletonBlock className="mt-2 h-3 w-2/3" /> : null}
                            <div className={cn('mt-4', isCourse && 'border-t border-[var(--landing-neutral-100)] pt-3')}>
                                <SkeletonBlock className="h-3 w-12" />
                                <SkeletonBlock className="mt-2 h-6 w-24" />
                            </div>
                            <SkeletonBlock className="mt-4 h-11 w-full bg-[var(--landing-primary-100)]" />
                        </div>
                    </div>
                </div>
            ))}
        </div>
    )
}

function PublicPricingSkeleton() {
    return (
        <div className={publicPricingStyles.sectionList}>
            {Array.from({length: 3}, (_, section) => (
                <div key={section}>
                    <div className={publicPricingStyles.sectionHeader}>
                        <SkeletonBlock className="size-10 shrink-0 !rounded-full bg-[var(--landing-primary-100)]" />
                        <SkeletonBlock className="h-7 w-52 max-w-[70%]" />
                    </div>
                    <div className={publicPricingStyles.desktopTable}>
                        <div className={cn(publicPricingStyles.tableGrid, publicPricingStyles.tableHeader)}>
                            {Array.from({length: 4}, (_, column) => <SkeletonBlock key={column} className="h-4 w-20" />)}
                        </div>
                        {Array.from({length: 2}, (_, row) => (
                            <div key={row} className={cn(publicPricingStyles.tableGrid, 'items-center gap-y-2 border-b border-slate-100 bg-white px-6 py-4 last:border-b-0')}>
                                <div className="space-y-2">
                                    <SkeletonBlock className="h-4 w-3/4" />
                                    <SkeletonBlock className="h-3 w-1/2" />
                                </div>
                                {Array.from({length: 3}, (_, column) => <SkeletonBlock key={column} className="h-4 w-20" />)}
                            </div>
                        ))}
                    </div>
                    <div className={publicPricingStyles.mobileList}>
                        {Array.from({length: 2}, (_, row) => (
                            <div key={row} className="rounded-[12px] border border-slate-100 bg-white p-4 shadow-sm">
                                <div className={publicPricingStyles.mobileCardHeader}>
                                    <SkeletonBlock className="h-4 w-40 max-w-full" />
                                    <div className={publicPricingStyles.mobilePriceBlock}>
                                        <SkeletonBlock className="h-3 w-12 sm:ml-auto" />
                                        <SkeletonBlock className="mt-1 h-4 w-20 sm:ml-auto" />
                                    </div>
                                </div>
                                <div className={publicPricingStyles.mobileMetaGrid}>
                                    {[0, 1].map(column => (
                                        <div key={column} className="space-y-2">
                                            <SkeletonBlock className="h-3 w-16" />
                                            <SkeletonBlock className="h-4 w-3/4" />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    )
}

function PublicContactSkeleton() {
    return (
        <>
            <div className="max-w-4xl space-y-3">
                <SkeletonBlock className="h-4 w-full" />
                <SkeletonBlock className="h-4 w-full" />
                <SkeletonBlock className="h-4 w-2/3" />
            </div>
            <div className="mt-8 grid gap-8 md:grid-cols-[minmax(0,0.9fr)_minmax(300px,0.75fr)] lg:grid-cols-[minmax(0,0.85fr)_minmax(360px,0.8fr)] lg:gap-12">
                <div className="min-w-0 space-y-4 md:order-2">
                    <SkeletonBlock className="h-4 w-28" />
                    <SkeletonBlock className="h-[180px] w-full !rounded-[12px] md:h-[220px]" />
                    <SkeletonBlock className="h-4 w-1/2" />
                    <SkeletonBlock className="h-4 w-3/4" />
                </div>
                <div className="min-w-0 space-y-7 md:order-1">
                    {[0, 1].map(section => (
                        <div key={section}>
                            <SkeletonBlock className="mb-3 h-4 w-36" />
                            <div className="divide-y divide-slate-100 border-y border-slate-100">
                                {Array.from({length: 3}, (_, row) => (
                                    <div key={row} className="flex items-center gap-4 py-3">
                                        <SkeletonBlock className={section === 0 ? 'size-5 shrink-0' : 'h-4 w-1/3'} />
                                        <SkeletonBlock className={cn('h-4', section === 0 ? 'w-2/3' : 'w-1/2')} />
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </>
    )
}

function PublicDocumentSkeleton({page}: {page: 'terms' | 'privacy'}) {
    return (
        <>
            {page === 'privacy' ? (
                <div className={publicEntryStyles.pageHeader}>
                    <div className={publicEntryStyles.pageHeaderRow}>
                        <SkeletonBlock className="h-12 w-80 max-w-full bg-[var(--landing-primary-100)] md:h-16 md:w-[560px]" />
                    </div>
                </div>
            ) : null}
            <div className={publicTermsStyles.contentShell}>
                <SkeletonBlock className="mb-8 h-8 w-80 max-w-full bg-[var(--landing-primary-100)]" />
                <div className="space-y-8">
                    {Array.from({length: 3}, (_, section) => (
                        <div key={section} className="space-y-3">
                            <SkeletonBlock className="mb-5 h-5 w-1/2" />
                            {Array.from({length: 4}, (_, line) => <SkeletonBlock key={line} className={cn('h-4', line === 3 ? 'w-3/4' : 'w-full')} />)}
                        </div>
                    ))}
                </div>
            </div>
        </>
    )
}

export function getPublicLoadingPadding(page: PublicSitePage, inherited = true) {
    if (page === 'about' || page === 'terms' || page === 'privacy') return publicEntryStyles.pagePaddingLoose
    if (page === 'pricing' || !inherited) return publicEntryStyles.pagePadding
    return publicEntryStyles.offerPagePadding
}

function PublicPageSkeleton({page}: {page: PublicSitePage}) {
    if (page === 'pricing') return <PublicPricingSkeleton />
    if (page === 'about') return <PublicContactSkeleton />
    if (page === 'terms' || page === 'privacy') return <PublicDocumentSkeleton page={page} />
    if (page === 'schedule') return <PublicScheduleSkeleton />
    if (page === 'privateBookings') return <PublicPrivateBookingsSkeleton />
    return <PublicOfferCardsSkeleton page={page} />
}

export function PublicPageContentSkeleton({page}: {page: PublicSitePage}) {
    const {t} = useTranslation('landing')
    const isOffer = page === 'schedule' || page === 'privateBookings' || page === 'courses' || page === 'events'

    return (
        <div role="status" aria-live="polite" aria-busy="true" aria-label={t('publicEntry.loading')} data-testid={isOffer ? 'public-offer-content-skeleton' : 'public-information-content-skeleton'} data-public-loading-page={page}>
            <div aria-hidden="true">
                <PublicPageSkeleton page={page} />
            </div>
        </div>
    )
}
